import { OverlayPortal as FloatingPortal } from "@/components/modal/OverlayPortal";
import type { JSX } from "react";
import { useDescendantExpanded, useDismissTooltipOnScrollAway } from "@/components/Tooltip.hooks";
import {
  TOOLTIP_FLOATING_LAYER_CLASS,
  TooltipSurfaceV2,
} from "@/components/interactive/ShortcutTooltipV2";
import { PropsWithClassName } from "@/interfaces/with-classname";
import { classNames } from "@/utils/classnames";
import {
  Placement,
  Strategy,
  autoUpdate,
  flip,
  offset,
  safePolygon,
  shift,
  useClientPoint,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
} from "@floating-ui/react";
import { Transition } from "@headlessui/react";
import React, {
  CSSProperties,
  Fragment,
  ReactNode,
  cloneElement,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import mergeRefs from "@/utils/merge-refs";

export type TooltipPlacement = Placement;
export const FloatingContext = createContext<HTMLElement | null>(null);

type PointerPosition = {
  clientX: number;
  clientY: number;
};

export interface TooltipV2Props extends PropsWithClassName {
  content: ReactNode;
  children: JSX.Element;
  disabled?: boolean;
  placement?: Placement;
  /** If true, tooltip will be open. Debugging tool, or if there's business logic that should force this tooltip to show */
  forceOpen?: boolean;
  light?: boolean;
  delay?: Delay;
  noArrow?: boolean;
  /** If true, this tooltip is shown without delay or fade in */
  instant?: boolean;
  /** Overrides the default 100ms cursor-rest threshold before the tooltip opens. */
  restMs?: number;
  style?: CSSProperties;
  offset?: number;
  /** If false, leaving the trigger closes the tooltip even if the pointer enters the tooltip body. */
  persistOnHover?: boolean;
  /** Distance from viewport edge before Floating UI shifts/flips the tooltip. */
  viewportPadding?: number;
  onOpen?: () => void;
  onClose?: () => void;
  strategy?: Strategy;
  /** Checked synchronously before an interaction opens the tooltip. */
  shouldOpen?: () => boolean;
  /** Prevent tooltips from coexisting with menus/popovers opened by the same trigger. */
  disableWhenTriggerExpanded?: boolean;
  /** Hide the tooltip without unmounting its trigger, for controls whose popup state lives elsewhere. */
  suppressOpen?: boolean;
  /**
   * Hide the tooltip while a menu opened from inside the trigger is up, until that menu closes.
   * For a trigger wrapping a dropdown whose button carries `aria-expanded`, which
   * `disableWhenTriggerExpanded` cannot see: that reads only the child element's own props.
   */
  suppressWhileDescendantExpanded?: boolean;
  fadeOnly?: boolean;
  /**
   * Anchor to the pointer rather than to the trigger's box, so the tooltip appears where the
   * pointer is instead of centred under the trigger. For a wide trigger, where box-anchoring puts
   * the tooltip far from what is being pointed at. Keyboard focus still anchors to the box, since
   * there is no pointer to follow.
   */
  followCursor?: boolean;
  /** Use dialog semantics and the larger surface for interactive tooltip content. */
  interactive?: boolean;
  /** Accessible name for interactive tooltip content. */
  interactiveLabel?: string;
}

export const TooltipV2Inner: React.FC<TooltipV2Props> = ({
  children,
  content,
  forceOpen,
  placement = "top",
  className,
  delay = { open: 500, close: 0 },
  instant,
  light: _light,
  noArrow,
  restMs,
  offset: tooltipOffset,
  persistOnHover = true,
  viewportPadding,
  onOpen,
  onClose,
  strategy,
  fadeOnly,
  followCursor = false,
  interactive = false,
  interactiveLabel,
  suppressOpen = false,
  suppressWhileDescendantExpanded = false,
  shouldOpen,
  disableWhenTriggerExpanded: _disableWhenTriggerExpanded,
  ...rest
}) => {
  const pointerPositionRef = useRef<PointerPosition | null>(null);
  const [open, setIsOpen] = useState(false);

  const onOpenChange = useCallback(
    (isOpen: boolean) => {
      if (isOpen && shouldOpen?.() === false) {
        return;
      }
      setIsOpen(isOpen);
      if (isOpen) {
        onOpen?.();
      } else {
        onClose?.();
      }
    },
    [onClose, onOpen, shouldOpen],
  );

  const floatingContext = useContext(FloatingContext);
  const resolvedTooltipOffset = tooltipOffset ?? 4;
  const resolvedViewportPadding = viewportPadding ?? 8;

  const { refs, floatingStyles, context, elements } = useFloating({
    placement,
    open: forceOpen || open,
    onOpenChange,
    strategy,
    middleware: [
      offset(resolvedTooltipOffset),
      flip({ padding: resolvedViewportPadding }),
      // If neither side fits, keep tall previews inside the viewport even if they overlap the trigger.
      shift({ padding: resolvedViewportPadding, crossAxis: true }),
    ],
    whileElementsMounted: autoUpdate,
  });
  const descendantExpanded = useDescendantExpanded(
    elements.domReference,
    suppressWhileDescendantExpanded,
  );
  const suppressed = suppressOpen || descendantExpanded;

  const hover = useHover(context, {
    enabled: !suppressed,
    move: false,
    delay: instant ? 0 : delay, //{ open: 500, close: 0 },
    restMs: instant ? 0 : restMs ?? 100,
    handleClose: persistOnHover ? safePolygon() : undefined,
  });
  const focus = useFocus(context, { enabled: !suppressed });
  const dismiss = useDismiss(context, { enabled: !suppressed });
  const role = useRole(context, {
    role: interactive ? "dialog" : "tooltip",
  });

  // Inert unless enabled, so every tooltip that has not opted in keeps anchoring to its trigger.
  const clientPoint = useClientPoint(context, { enabled: followCursor, axis: "both" });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
    dismiss,
    role,
    clientPoint,
  ]);

  const updatePointerPosition = useCallback((event: React.PointerEvent) => {
    pointerPositionRef.current = {
      clientX: event.clientX,
      clientY: event.clientY,
    };
  }, []);

  const isPointerOverReference = useCallback(() => {
    const reference = refs.reference.current;
    if (!(typeof Element !== "undefined" && reference instanceof Element)) {
      return false;
    }

    const pointerPosition = pointerPositionRef.current;
    if (pointerPosition == null) {
      return reference.matches(":hover");
    }

    const elementAtPointer = reference.ownerDocument.elementFromPoint(
      pointerPosition.clientX,
      pointerPosition.clientY,
    );

    return elementAtPointer != null && reference.contains(elementAtPointer);
  }, [refs.reference]);

  React.useEffect(() => {
    if (!suppressed || !open) {
      return;
    }

    setIsOpen(false);
    onClose?.();
  }, [onClose, open, suppressed]);

  const resolvedOpen = forceOpen || (!suppressed && open);

  useDismissTooltipOnScrollAway({
    open: resolvedOpen,
    forceOpen,
    referenceElement: refs.reference.current,
    floatingElement: persistOnHover ? elements.floating : null,
    isPointerOverReference,
    onOpenChange,
  });

  // React 19 made `ref` an ordinary prop, so spreading children.props after ours
  // overwrote the reference callback, leaving the tooltip no anchor to position
  // against. Set it last, merged so forwardRef children keep their own ref.
  const childRef = (children.props as { ref?: React.Ref<Element> }).ref;
  const setReferenceRef = useMemo(
    () => mergeRefs<Element>(refs.setReference, childRef ?? null),
    [refs.setReference, childRef],
  );

  return (
    <>
      {cloneElement(
        children,
        getReferenceProps({
          ...children.props,
          ref: setReferenceRef,
          onPointerEnter: (event: React.PointerEvent) => {
            children.props.onPointerEnter?.(event);
            updatePointerPosition(event);
          },
          onPointerMove: (event: React.PointerEvent) => {
            children.props.onPointerMove?.(event);
            updatePointerPosition(event);
          },
        }),
      )}
      {resolvedOpen && (
        <FloatingPortal root={floatingContext}>
          <div
            ref={refs.setFloating}
            data-row-click-ignore="true"
            style={{
              ...floatingStyles,
              maxHeight: `calc(100dvh - ${2 * resolvedViewportPadding}px)`,
            }}
            {...getFloatingProps({
              "aria-label": interactive ? interactiveLabel : undefined,
              className: classNames(
                TOOLTIP_FLOATING_LAYER_CLASS,
                !persistOnHover && "pointer-events-none",
              ),
            })}
          >
            <Transition
              as={Fragment}
              show={resolvedOpen}
              enter={instant ? "" : "transition ease-out duration-[150ms]"}
              enterFrom={fadeOnly ? "opacity-0" : "transform opacity-0 scale-95"}
              enterTo={fadeOnly ? "opacity-100" : "transform opacity-100 scale-100"}
              leave={instant ? "" : "transition ease-in duration-[100ms]"}
              leaveFrom={fadeOnly ? "opacity-100" : "transform opacity-100 scale-100"}
              leaveTo={fadeOnly ? "opacity-0" : "transform opacity-0 scale-95"}
            >
              <TooltipSurfaceV2
                className={classNames("max-h-[inherit] overflow-y-auto", className)}
                variant={interactive ? "interactive" : "content"}
                {...rest}
              >
                {content}
              </TooltipSurfaceV2>
            </Transition>
          </div>
        </FloatingPortal>
      )}
    </>
  );
};

type Delay = number | Partial<{ open: number; close: number }>;

const isExpandedAriaValue = (value: unknown) => value === true || value === "true";

export const TooltipV2: React.FC<TooltipV2Props> = ({
  children,
  disabled,
  disableWhenTriggerExpanded = true,
  ...tooltipProps
}) => {
  const tooltipContent = tooltipProps.content;
  const isTooltipEmpty =
    tooltipContent == null || (typeof tooltipContent === "string" && !tooltipContent);
  const isTriggerExpanded =
    disableWhenTriggerExpanded &&
    !tooltipProps.forceOpen &&
    isExpandedAriaValue(children.props["aria-expanded"]);
  return disabled || isTooltipEmpty || isTriggerExpanded ? (
    children
  ) : (
    <TooltipV2Inner {...tooltipProps} disableWhenTriggerExpanded={disableWhenTriggerExpanded}>
      {children}
    </TooltipV2Inner>
  );
};

export const TooltipNoPersist: React.FC<TooltipV2Props> = ({
  persistOnHover = false,
  ...props
}) => {
  return <TooltipV2 persistOnHover={persistOnHover} {...props} />;
};

export const Tooltip = TooltipV2;
export type TooltipProps = TooltipV2Props;
